import { useState } from "react";
import { Link } from "react-router-dom";
import { FaCircle } from "react-icons/fa";
import { FaMinusCircle } from "react-icons/fa";
import { FaDotCircle } from "react-icons/fa";
import { Dropdown } from "react-bootstrap";
import { Button } from "@mui/joy";
import { agentLoggedIn } from "@/Store/Reducers/loggedInAgentSlice";
import ContactSupportModal from "@/Components/Utility/Modals/ContactSupportModal";
import { useAppDispatch } from "@/Store/hooks";
import { AgentAvatar } from "@/Components/Avatar/AgentAvatar";
import AddAgentModal from "@/Components/Utility/Modals/AddAgentModal";
import Feed from "@/Pages/home/Feed";
import { ApiUpdateEntity } from "@/legacy/ApiCallerOld";
import { useAgent } from "@/Hooks/useAgent";
import { AgentStatus } from "@shared/Enums/Enums";
import { Agent } from "@shared/Entities/EntityTypes";
import { Entities } from "@shared/Entities/Entities";

export default function Home() {
	document.title = "Home | GoDesk";

	return (
		<div className="h-full">
			<div className="flex items-center px-3 border-b border-gray-300" style={{ height: "6em" }}>
				<div style={{ width: "30em" }}>
					<AgentCard />
				</div>
			</div>

			<div className="flex" style={{ height: "calc(100% - 6em)" }}>
				<div className="flex flex-col border-r border-gray-300 py-3 shrink-0" style={{ width: "30em" }}>
					<div className="mb-2 text-base font-semibold px-3">Recent updates</div>
					<div className="overflow-y-auto px-3 grow">
						<Feed />
					</div>
				</div>

				<div className="grow" style={{ backgroundColor: "white", overflowY: "auto" }}>
					<GetStartedGuide />
				</div>
			</div>
		</div>
	);
}

function GetStartedGuide() {
	const [contactSupportModalOpen, setContactSupportModalOpen] = useState(false);
	const [addAgentModalOpen, setAddAgentModalOpen] = useState(false);

	// const sse = new EventSource("http://localhost:8080/api/gpt/sse");

	fetch("http://localhost:8080/api/gpt/sse")
		.then((response) => {
			if (response.body == null) {
				throw "Could not get body";
			}

			const reader = response.body.getReader();

			return new ReadableStream({
				start(controller) {
					// The following function handles each data chunk
					function push() {
						// "done" is a Boolean and value a "Uint8Array"
						reader.read().then(({ done, value }) => {
							// If there is no more data to read
							if (done) {
								console.log("done", done);
								controller.close();
								return;
							}
							// Get the data and send it to the browser via the controller
							controller.enqueue(value);


							// Check chunks by logging to the console
							console.log(done, value, new TextDecoder().decode(value));
							push();
						});
					}

					push();
				},
			});
		})
		.then((stream) =>{
		// Respond with our stream
			console.log("next");
			return new Response(stream, { headers: { "Content-Type": "text/html" } });

		})
		.then((result) => {
			// Do things with result
			console.log(result);
		});


	return (
		<div className="p-10">
			<div className="font-semibold text-3xl">
				Welcome to GoDesk!
			</div>
			<div className="mt-5 mb-10">
				<div className="mb-4">
					You've just taken your first step to better customer service. Follow the steps below to begin setting up your help desk:
				</div>
			</div>
			<div className="my-10">
				<div className="flex items-center font-semibold text-xl">
					<img className="h-7 mr-3" src="/emoji-envelope.png" />
					Connect Your Emails
				</div>
				<div className="my-3">
					Get started by linking your mailbox and pulling in your first emails.
				</div>

				<Link to="/config/manage/channels/email/-1" className="mx-auto">
					<Button>
						Link mailbox
					</Button>
				</Link>
			</div>
			<div className="my-10">
				<div className="flex items-center font-semibold text-xl">
					<img className="h-7 mr-3" src="/invite.svg" />
					Invite Your Team
				</div>
				<div className="my-3">
					Add the people you work with to GoDesk. They will be sent an email invite with instructions to join you.
				</div>
				<Button onClick={() => setAddAgentModalOpen(true)}>
					Invite agents
				</Button>
			</div>

			<iframe
				width="400"
				height="225"
				src="https://www.youtube-nocookie.com/embed/d6q_3tboyw4?si=scY_G7a8Gur1jK0k"
				title="YouTube video player"
				frameBorder="0"
				allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
				referrerPolicy="strict-origin-when-cross-origin"
				allowFullScreen
			/>

			<AddAgentModal isOpen={addAgentModalOpen} closeModal={() => setAddAgentModalOpen(false)} />
			<ContactSupportModal isOpen={contactSupportModalOpen} closeModal={() => setContactSupportModalOpen(false)} />
		</div>
	);
}

function AgentCard() {
	const agent = useAgent();

	return (
		<div className="flex">
			<div style={{ marginRight: "10px" }}>
				<AgentAvatar agent={agent} widthPx={50} />
			</div>
			<div>
				<div>{agent?.name}</div>
				<div>{agent?.jobTitle}</div>
				<StatusSelector />
			</div>
		</div>
	);
}

function StatusSelector() {
	const dispatch = useAppDispatch();
	const agent = useAgent();

	const label_offline = <><FaCircle color="green" /> OFFLINE</>;
	const label_available = <><FaCircle color="green" /> Available</>;
	const label_idle = <><FaCircle color="#FAA61A" /> Idle</>;
	const label_dnd = <><FaMinusCircle color="red" /> Do Not Disturb</>;
	const label_ooo = <><FaDotCircle color="grey" /> Out Of Office</>;

	let toggleLabel = null;

	switch (agent?.status) {
		case AgentStatus.OFFLINE: toggleLabel = label_offline; break;
		case AgentStatus.AVAILABLE: toggleLabel = label_available; break;
		case AgentStatus.IDLE: toggleLabel = label_idle; break;
		case AgentStatus.DND: toggleLabel = label_dnd; break;
		case AgentStatus.OOO: toggleLabel = label_ooo; break;
		default: toggleLabel = label_offline; break;
	}

	async function onSelect(eventKey: string | null) {
		if (eventKey != null && agent != null) {
			// Post updated agent.
			const agentCopy = { ...agent };
			agentCopy.status = parseInt(eventKey);

			const ApiResponse = await ApiUpdateEntity<Agent>(Entities.AGENT, agent.id, agentCopy);

			if (ApiResponse.data != null) {
				// Update LoggedInAgent
				dispatch(agentLoggedIn(ApiResponse.data));
			}
		}
	}

	return (
		<Dropdown onSelect={onSelect}>
			<Dropdown.Toggle as="span">
				{toggleLabel}
			</Dropdown.Toggle>

			<Dropdown.Menu>
				<Dropdown.Item eventKey={AgentStatus.AVAILABLE.toString()}>{label_available}</Dropdown.Item>
				<Dropdown.Item eventKey={AgentStatus.IDLE.toString()}>{label_idle}</Dropdown.Item>
				<Dropdown.Item eventKey={AgentStatus.DND.toString()}>{label_dnd}</Dropdown.Item>
				<Dropdown.Item eventKey={AgentStatus.OOO.toString()}>{label_ooo}</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);
}
